/* -------------------------- Design imports start -------------------------- */
import { DeleteOutlined, ShareRounded } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import SmartTable from '../../../components/SmartTable'
import { type SmartTableColumnType } from '../../../utils/types'
/* --------------------------- Design imports end --------------------------- */
/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement, useState } from "react";
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'

import CreateGuestRequestSharepointDrawer from './Sharepoint/CreateGuestRequestSharepointDrawer'
import UnauthRequestDrawer from './Drawer/UnauthRequestDrawer'
import { GuestRequestInfoPanelContent } from '../../Customer/GuestRequestInfoPanel'
import GetConstant from '../utils/constants'
import { useGuestRequests } from "../hooks/useGuestRequestQueries";
import { useDeleteGuestRequests } from '../hooks/useGuestRequestMutations'
import { type GuestRequest } from '../utils/types'
import { guestRequestKeys } from '../utils/queryKeys'
import Button from '../../../components/Button'
import FilesDrawer from '../../Filesystem/components/FilesDrawer'
import DeleteModal from '../../../components/widgets/DeleteModal'
import LogTool from '../../../logger/logTools'
import { useCountContext } from '../../../utils/context'
import { type FileInfo } from '../../../utils/types'
import { inputToAPISharepointJSON } from '../../Item/api/articleApi'
import { useCreateSharepoint } from '../../Item/hooks/useArticleMutations'
import { Stack } from "@mui/system";
import { Sharepoint } from "../../Item/utils/types";
import { markEntityAsRead } from '../../../api/unreadOverview'
import { deriveFilesUrl } from '../../Filesystem/utils/filesHelper';
/* ------------------------- Functional import end ------------------------- */
type Props = {
  setSelectedGuestRequest: React.Dispatch<React.SetStateAction<GuestRequest | undefined>>
  openFilesDrawer: boolean
  setOpenFilesDrawer: React.Dispatch<React.SetStateAction<boolean>>
  selectedGuestRequest: GuestRequest | undefined
}

/* -------------------------------------------------------------------------- */
/*                               Start component                              */
/* -------------------------------------------------------------------------- */
export default function UnauthRequestOverviewPage(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const log = new LogTool({ context: "UnauthRequestOverviewPage", enable: false, logLevel: "debug" });
  const { t } = useTranslation();
  const {
    setSelectedGuestRequest,
    openFilesDrawer,
    setOpenFilesDrawer,
    selectedGuestRequest
  } = props;
  const {
    initialGuestRequests,
    setInitialGuestRequestsCount,
    setCountTotals,
    guestRequestQueryOptions,
    setGuestRequestQueryOptions,
  } = useCountContext();
  const queryClient = useQueryClient();
  const createSharepointMutation = useCreateSharepoint();
  const deleteGuestRequestsMutation = useDeleteGuestRequests();

  /* --------------------------- Non state data end --------------------------- */
  /* ---------------------------- Flag states start --------------------------- */
  const [openDelete, setOpenDelete] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openShareDrawer, setOpenShareDrawer] = useState(false);
  /* ----------------------------- Flag states end ---------------------------- */
  /* --------------------------- Data states start ---------------------------- */
  const [selectedGuestRequests, setSelectedGuestRequests] = useState<GuestRequest[]>([]);
  const [guestRequestFormInput, setGuestRequestFormInput] = useState<GuestRequest | undefined>(undefined);
  const [guestRequestSharepointFormInput, setGuestRequestSharepointFormInput] = useState<any>({});
  const [newGuestRequests, setNewGuestRequests] = useState<GuestRequest[]>(initialGuestRequests);
  /* ---------------------------- Data states end ----------------------------- */

  /* ------------------------------ Effects start ----------------------------- */

  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  async function markGuestRequestAsRead(requestItem: GuestRequest): Promise<void> {
    const requestId = Number(requestItem.key)
    if (Number.isNaN(requestId)) {
      log.warn('Unable to resolve guest request id for marking as read', requestItem)
      return
    }

    // Optimistic update directly on all guest request list queries
    queryClient.setQueriesData(
      { queryKey: guestRequestKeys.lists(), exact: false },
      (oldData: any) => {
        if (!oldData || !oldData.results) return oldData
        return {
          ...oldData,
          results: oldData.results.map((item: GuestRequest) =>
            item.key === requestItem.key ? { ...item, read: true } : item
          )
        }
      }
    )

    // Update counts for tab badges
    setInitialGuestRequestsCount(prev => Math.max(0, prev - 1))
    setCountTotals(prev => Math.max(0, prev - 1))

    try {
      await markEntityAsRead('guest_request', requestId)
    } catch (error) {
      log.error('Error marking guest request as read', error)
      toast.error(t('request:feedback.error.markAsRead', 'Fehler beim Aktualisieren des Lesestatus'))
  }
  }

  const handleCreateGuestRequestSharepoint = async (sharepointInput: any): Promise<Sharepoint | 'ERROR'> => {
    log.info("Begin creating sharepoint for guest request");
    const json = inputToAPISharepointJSON(sharepointInput);
    log.debug("Sharepoint JSON ->", json);

    try {
      const response = await createSharepointMutation.mutateAsync(json);
      log.info("Success creating sharepoint for guest request");
      return response;
    } catch (error) {
      log.error("Error creating sharepoint for guest request", error);
      return "ERROR";
    }
  };
  const handleDeleteGuestRequests = async (): Promise<void> => {
    const requestsToDelete = selectedGuestRequests.map(request => request.key);

    // Clear selection before mutation to prevent warning about selected items
    // that are no longer visible (mutation's onSuccess invalidates queries)
    flushSync(() => {
      setSelectedGuestRequests([]);
      setSelectedGuestRequest(undefined);
      setOpenDelete(false);
    });

    try {
      await deleteGuestRequestsMutation.mutateAsync(requestsToDelete);
      log.info('Success deleting guest requests');
      toast.success(t('request:feedback.success.deleteRequest'));
    } catch (error) {
      log.error('Error deleting guest requests:', error);
      toast.error(t('request:feedback.error.deleteRequest'));
    }
  };
  /* ------------------------- Callback functions end ------------------------- */
  /* ------------------------- Render constants start ------------------------- */
  // GetConstant calls hooks internally, so it cannot be inside useMemo
  const allColumns = GetConstant({
    name: "guestRequestTableColumns",
  }) as SmartTableColumnType<GuestRequest>[];

  const guestRequestTableActions = (
    <Stack direction='row' spacing={1} alignItems={'center'}>
      <Button
        id="editRequestButton"
        size="small"
        variant="outlined"
        color="primary"
        onClick={() => {
          setGuestRequestFormInput(selectedGuestRequest);
          setOpenDrawer(true);
        }}
        disabled={selectedGuestRequests.length !== 1}
      >
        {t("common:interaction.button.editRequest")}
      </Button>
      <Button
        id="deleteRequestButton"
        size="small"
        variant="outlined"
        color="primary"
        startIcon={<DeleteOutlined />}
        onClick={() => setOpenDelete(true)}
        disabled={selectedGuestRequests.length < 1}
      >
        {t("request:interaction.button.deleteRequest")}
      </Button>
      <IconButton
        aria-label="share"
        disabled={selectedGuestRequests.length !== 1}
        onClick={() => setOpenShareDrawer(true)}
      >
        <ShareRounded />
      </IconButton>
    </Stack>
  )
  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */
  let guestRequestFiles: FileInfo[] | string = [];
  if (selectedGuestRequest) {
    if (typeof selectedGuestRequest.files === "string") {
      guestRequestFiles = selectedGuestRequest.files;
    } else if (Array.isArray(selectedGuestRequest.files) && selectedGuestRequest.files.length > 0) {
      guestRequestFiles = selectedGuestRequest.files;
    } else {
      const filesUrl = deriveFilesUrl(selectedGuestRequest);
      if (filesUrl) {
        guestRequestFiles = filesUrl;
      }
    }
  }
  /* ------------------------- Pre render actions end ------------------------- */

  log.debug(
    "selectedGuestRequest.files ->",
    selectedGuestRequest,
    "guestRequestFiles ->",
    guestRequestFiles
  );

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <>
      <SmartTable
        tableId="unauthRequestTable"
        columnsConfig={{
          allColumns: allColumns,
          initialVisibleColumnIndices: [
            'self', 'email', 'company', 'phone', 'quantity', 'material', 'message',
            'status', 'createdAt', 'editors', 'remarks'
          ]
        }}
        queryConfig={{
          dataHook: useGuestRequests,
          initialQueryOptions: guestRequestQueryOptions
        }}
        queryState={{
          options: guestRequestQueryOptions,
          setOptions: setGuestRequestQueryOptions,
        }}
        selection={{
          state: selectedGuestRequests,
          setState: setSelectedGuestRequests,
          onChange: (selected) => {
            if (selected.length === 1) {
              const record = selected[0]
              // Mark as read if unread
              if (!record.read) {
                void markGuestRequestAsRead(record)
              }
              setSelectedGuestRequest(record)
            } else if (selected.length === 0) {
              setSelectedGuestRequest(undefined)
            }
          },
        }}
        actions={guestRequestTableActions}
        customInfoPanel={(open, setOpen) => (
          <GuestRequestInfoPanelContent
            setOpenFilesDrawer={setOpenFilesDrawer}
            infoData={selectedGuestRequest}
          />
        )}
        onRow={{
          onClick: (record) => {
            setSelectedGuestRequests([record])
            setSelectedGuestRequest(record)
          },
        }}
        contextMenuOptions={[
          {
            label: t('common:interaction.button.showFiles'),
            onClick: () => setOpenFilesDrawer(true)
          }
        ]}
      />
      <DeleteModal
        open={openDelete}
        setOpen={setOpenDelete}
        onDelete={handleDeleteGuestRequests}
        name={t('common:content.label.requests')}
      />
      <UnauthRequestDrawer
        open={openDrawer}
        setOpen={setOpenDrawer}
        formState={{ state: guestRequestFormInput, setState: setGuestRequestFormInput }}
        onClose={() => {
          setSelectedGuestRequests([])
          setSelectedGuestRequest(undefined)
        }}
      />
      {openFilesDrawer && (
        <FilesDrawer
          open={openFilesDrawer}
          setOpen={setOpenFilesDrawer}
          /* eslint-disable-next-line @typescript-eslint/no-empty-function */
          onClose={() => {}}
          files={guestRequestFiles}
          title={t('common:content.label.requestFiles') }
        />
      )}
      {selectedGuestRequest && (
        <CreateGuestRequestSharepointDrawer
          open={openShareDrawer}
          setOpen={setOpenShareDrawer}
          formState={{
            state: guestRequestSharepointFormInput,
            setState: setGuestRequestSharepointFormInput,
          }}
          selectedGuestRequest={selectedGuestRequest}
          onConfirm={handleCreateGuestRequestSharepoint}
        />
      )}
    </>
  )
}
